/**
 * Textarea Component
 * A styled textarea input component with hint and error support.
 * @packageDocumentation
 */

import { useId, forwardRef, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * Props for the Textarea component.
 */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Help text displayed below the textarea. */
  hint?: string;
  /** Error message displayed below the textarea. */
  error?: string;
}

/**
 * Textarea component with consistent styling, hint, and error support.
 *
 * @param { TextareaProps } props Component props.
 * @returns { JSX.Element } The textarea element.
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ hint, error, className, id: providedId, 'aria-describedby': ariaDescribedBy, ...props }, ref) => {
    const generatedId = useId();
    const textareaId = providedId ?? generatedId;
    const errorId = error ? `${textareaId}-error` : undefined;
    const hintId = hint && !error ? `${textareaId}-hint` : undefined;

    const describedByIds = [errorId, hintId, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

    return (
      <div className="w-full">
        <textarea
          id={textareaId}
          ref={ref}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedByIds}
          className={cn(
            'flex min-h-[60px] w-full rounded-sm border bg-transparent px-3 py-2 text-base shadow-sm transition-[border-color,box-shadow] duration-150',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'md:text-sm',
            error ? 'border-destructive focus-visible:ring-destructive' : 'border-input',
            className,
          )}
          {...props}
        />
        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} className="mt-1.5 text-xs text-destructive" role="alert" aria-live="polite">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
